import { API_GET, API_POST } from '../../api/api';
import { request } from './aiChatApi';
import { aiGrantsFor } from './aiPermissionsCookie';
import { POSITION_SORT_PURPOSE, latestArrangement } from './positionSort';

const POLL_INTERVAL = 3000;
const TIMEOUT = 5 * 60 * 1000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Runs a "Sort with AI" in the background: a fresh purpose chat
 * (position_sort) gets the prompt from buildSortRequest(), the turn is
 * awaited by polling the chat, and the arrangement the model handed over
 * with propose_arrangement is returned ({ callId, items }). There is
 * nobody at the chat, so anything the turn asks for (a permission, a
 * question) is rejected and the turn continues. Rejects with an Error
 * whose message names the reason: the API message, "failed",
 * "no_arrangement" or "timeout".
 */
export async function runAiPositionSort({
  config,
  document,
  prompt,
  refs,
  groupRefs,
  projectId = null,
}) {
  const created = await request(
    config,
    'ai-chats',
    API_POST({
      title: 'Sort with AI',
      purpose: POSITION_SORT_PURPOSE,
      purposeData: { document, refs, groupRefs },
      ...(projectId ? { projectId } : {}),
    }),
  );
  const chatId = created.data?.id;
  if (!chatId) throw new Error('failed');

  await request(
    config,
    `ai-chats/${chatId}/messages`,
    API_POST({
      prompt,
      attachments: [],
      references: [],
      grantedPermissions: aiGrantsFor(chatId),
    }),
  );

  const startedAt = Date.now();
  let finished = false;
  while (Date.now() - startedAt < TIMEOUT) {
    await sleep(POLL_INTERVAL);
    const res = await request(config, `ai-chats/${chatId}`, API_GET);
    const status = res.data?.status;

    if (status === 'awaiting_approval') {
      const pending = res.data?.pendingApprovals || [];
      if (pending.length)
        await request(
          config,
          `ai-chats/${chatId}/approvals`,
          API_POST({
            decisions: pending.map((approval) => ({
              id: approval.id,
              decision: 'reject',
            })),
            grantedPermissions: aiGrantsFor(chatId),
          }),
        ).catch(() => {});
      continue;
    }
    if (status === 'failed')
      throw new Error(res.data?.lastError?.message || 'failed');
    if (status === 'idle') {
      finished = true;
      break;
    }
  }
  if (!finished) throw new Error('timeout');

  const messages = await request(
    config,
    `ai-chats/${chatId}/messages?perPage=50`,
    API_GET,
  );
  // newest first from the API
  const arrangement = latestArrangement(
    [...(messages.data?.messages || [])].reverse(),
  );
  if (!arrangement) throw new Error('no_arrangement');
  return arrangement;
}
